<template>
    <v-form ref="form" @submit.prevent>
        <v-layout class="password-creation" row wrap>
            <v-flex xs12 py-0>
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Anrede*' | translation }}</p>
                <v-select
                    v-model="salutation"
                    :items="salutations"
                    :rules="[rules.required]"
                    :label="inputLabelOutside ? '' : $language.translate('Anrede*')"
                    required
                    :solo="inputSolo"
                    :disabled="disabled"
                >
                </v-select>
            </v-flex>

            <v-flex xs12 py-0>
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Vorname*' | translation }}</p>
                <v-text-field
                    v-model="firstName"
                    :rules="[rules.required, rules.firstName]"
                    :label="inputLabelOutside ? '' : $language.translate('Vorname*')"
                    required
                    :solo="inputSolo"
                    :disabled="disabled"
                ></v-text-field>
            </v-flex>

            <v-flex xs12 py-0>
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Nachname*' | translation }}</p>
                <v-text-field
                    v-model="lastName"
                    :rules="[rules.required]"
                    :label="inputLabelOutside ? '' : $language.translate('Nachname*')"
                    required
                    :solo="inputSolo"
                    :disabled="disabled"
                ></v-text-field>
            </v-flex>

            <v-flex xs12 py-0 v-if="!isUserLoggedIn">
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Email*' | translation }}</p>
                <v-text-field
                    v-model="email"
                    :rules="[rules.required, rules.email]"
                    :label="inputLabelOutside ? '' : $language.translate('Email*')"
                    required
                    :solo="inputSolo"
                    :error-messages="errors['email']"
                    @change="resetErrors('email')"
                    :disabled="disabled"
                ></v-text-field>
            </v-flex>
            <v-flex xs12 py-0>
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Telefonnummer*' | translation }}</p>
                <v-text-field
                    v-model="phone"
                    :rules="[rules.required, rules.phone]"
                    :label="inputLabelOutside ? '' : $language.translate('Telefonnummer*')"
                    required
                    :solo="inputSolo"
                    :error-messages="errors['phone']"
                    @change="resetErrors('phone')"
                    :disabled="disabled"
                ></v-text-field>
            </v-flex>

            <v-flex xs12 v-if="allowGuest && !isUserLoggedIn" py-0>
                <v-radio-group v-model="askPassword" row :disabled="disabled">
                    <v-radio :label="$language.translate('Konto anlegen')" :value="true"></v-radio>

                    <v-tooltip v-if="isGuestDisabled" top open-on-hover open-on-click>
                        <template v-slot:activator="{ on }">
                            <div v-on="on" class="d-flex align-center">
                                <v-radio
                                    :label="$language.translate('Als Gast bestellen')"
                                    :value="false"
                                    :disabled="true"
                                ></v-radio>
                            </div>
                        </template>
                        <span>
                            {{
                                $language.translate(
                                    'Gastbestellung ist für Firmenkunden nicht erlaubt. Bitte registrieren Sie sich oder bestellen Sie als Privatkunde.',
                                )
                            }}
                        </span>
                    </v-tooltip>
                    <template v-else>
                        <v-radio :label="$language.translate('Als Gast bestellen')" :value="false"></v-radio>
                    </template>
                </v-radio-group>
            </v-flex>

            <v-flex xs12 v-if="!isUserLoggedIn && (askPassword || !allowGuest)" py-0>
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Passwort*' | translation }}</p>
                <v-text-field
                    v-model="password"
                    :append-icon="showPassword ? 'visibility_off' : 'visibility'"
                    :rules="[rules.required, rules.length]"
                    :type="showPassword ? 'text' : 'password'"
                    @click:append="showPassword = !showPassword"
                    :label="inputLabelOutside ? '' : $language.translate('Passwort*')"
                    required
                    :solo="inputSolo"
                    browser-autocomplete="new-password"
                    :error-messages="errors['password']"
                    @change="resetErrors('password')"
                    :disabled="disabled"
                ></v-text-field>
            </v-flex>

            <v-flex xs12 py-0 v-if="isUserLoggedIn || askPassword">
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ getCompanyLabel() }}</p>
                <v-text-field
                    v-model="company"
                    :label="inputLabelOutside ? '' : getCompanyLabel()"
                    :rules="[rules.company]"
                    :required="!getIsPrivateCustomer"
                    :error-messages="errors['company']"
                    @change="resetErrors('company')"
                    :disabled="disabled"
                    ref="companyField"
                ></v-text-field>
            </v-flex>

            <v-flex xs12 py-0 v-if="isUserLoggedIn || askPassword">
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ getUidLabel() }}</p>
                <v-text-field
                    v-model="uid"
                    :label="inputLabelOutside ? '' : getUidLabel()"
                    :rules="[rules.uid]"
                    :required="!getIsPrivateCustomer"
                    @input="validateUid"
                    :disabled="disabled"
                    :error="!validUid"
                    :error-messages="errorUidMessages"
                    ref="companyUidField"
                ></v-text-field>
                <div class="v-messages theme--light">
                    <div class="v-messages__message">
                        Um diese Funktion zu nutzen brauchen Sie ein Kundenkonto. Bitte loggen Sie sich ein oder
                        registrieren Sie sich. Steuernummer/UST-ID: Für unsere EU Geschäftskunden. Wenn sie eine
                        steuerfreie Rechnung wünschen geben sie bitte ihre UID Nummer ohne die Länderkennung an (z.B.
                        U12345 statt ATU12345, oder 12345 statt DE12345). Nach automatischer Prüfung der Nummer erhalten
                        Sie dann eine Rechnung ohne Umsatzsteuer.
                    </div>
                </div>
            </v-flex>
        </v-layout>
    </v-form>
</template>

<script>
import Vue from 'vue';
import gql from 'graphql-tag';
import module from '../../store/registration';
import { mapState, mapGetters, createNamespacedHelpers } from 'vuex';
import Login from '@breakmedia/prime-vue-customer/src/Login/LoginForm';

const moduleName = 'Registration';
const checkoutAddressModuleName = 'CheckoutAddressStep';

const { mapMutations } = createNamespacedHelpers(moduleName);

if (!Vue.$store.state[moduleName]) {
    Vue.$store.registerModule(moduleName, module);
}

export default {
    name: moduleName,
    data: function() {
        return {
            showPassword: false,
            rules: {
                required: value => !!value || this.$language.translate('This field is required and cannot be empty.'),
                length: value => (value ? value.length >= 7 || 'Das Passwort muss mindestens 7 Zeichen haben.' : ''),
                email: value => /.+@.+/.test(value) || 'Bitte geben sie eine gültige Email-Adresse ein.',
                phone: value => /^(\+)?\d{5,15}$/g.test(value) || 'Bitte geben sie eine gültige Telefonnummer ein.',
                firstName: value =>
                    value
                        ? new RegExp('[a-zA-Z]{2}', 'i').test(value) || 'Das Vorname muss mindestens 2 Zeichen haben.'
                        : '',
                company: value => {
                    if (!value && !this.getIsPrivateCustomer) {
                        return this.$language.translate('This field is required and cannot be empty.');
                    }
                    return true;
                },
                uid: value => {
                    if (!value && !this.getIsPrivateCustomer) {
                        return this.$language.translate('This field is required and cannot be empty.');
                    }
                    return true;
                },
            },
            salutations: [
                {
                    text: 'Frau',
                    value: 'frau',
                },
                {
                    text: 'Herr',
                    value: 'herr',
                },
            ],
            validUid: {},
            errorUidMessages: [],
        };
    },
    props: {
        allowGuest: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        inputSolo: Boolean,
        inputLabelOutside: Boolean,
    },
    mounted() {
        if (!this.isUserLoggedIn) {
            const registrationData = this.$session.get('registration-data') || {};
            Object.keys(registrationData).forEach(key => {
                this[key] = registrationData[key];
            });
        }
        // Ensure guest checkout is deselected when Firmenkunde is active on mount
        if (this.isGuestDisabled && this.allowGuest && !this.isUserLoggedIn) {
            this.askPassword = true;
        }
    },
    computed: {
        ...mapState(moduleName, {
            errors: state => state.errors,
        }),
        ...mapGetters(Login.name, ['isUserLoggedIn']),
        ...mapGetters(checkoutAddressModuleName, ['getIsPrivateCustomer']),
        salutation: {
            get() {
                return this.getSalutation();
            },
            set(value) {
                this.setSalutation(value);
            },
        },
        firstName: {
            get() {
                return this.getFirstName();
            },
            set(value) {
                this.setFirstName(value);
            },
        },
        lastName: {
            get() {
                return this.getLastName();
            },
            set(value) {
                this.setLastName(value);
            },
        },
        email: {
            get() {
                return this.getEmail();
            },
            set(value) {
                this.setEmail(value);
            },
        },
        phone: {
            get() {
                return this.getPhone();
            },
            set(value) {
                this.setPhone(value);
            },
        },
        company: {
            get() {
                return this.getCompany();
            },
            set(value) {
                this.setCompany(value);
            },
        },
        uid: {
            get() {
                return this.getUid();
            },
            set(value) {
                this.setUid(value);
            },
        },
        password: {
            get() {
                return this.getPassword();
            },
            set(value) {
                this.setPassword(value);
            },
        },
        askPassword: {
            get() {
                return this.getAskPassword();
            },
            set(value) {
                this.setAskPassword(value);
            },
        },
        isGuestDisabled() {
            return !this.getIsPrivateCustomer;
        },
    },
    watch: {
        getIsPrivateCustomer(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (this.$refs.companyUidField && this.$refs.companyUidField.resetValidation) {
                    this.$refs.companyUidField.resetValidation();
                }
                if (this.$refs.companyField && this.$refs.companyField.resetValidation) {
                    this.$refs.companyField.resetValidation();
                }
                // If Firmenkunde is selected, ensure guest checkout is deselected
                if (!newVal && this.allowGuest && !this.isUserLoggedIn) {
                    this.askPassword = true;
                }
            }
        },
        // When switching to guest checkout, clear fields that are hidden
        askPassword(newVal, oldVal) {
            if (oldVal !== undefined && newVal === false) {
                this.setPassword('');
                this.setCompany('');
                this.setUid('');
                if (this.$refs.companyUidField && this.$refs.companyUidField.resetValidation) {
                    this.$refs.companyUidField.resetValidation();
                }
                if (this.$refs.companyField && this.$refs.companyField.resetValidation) {
                    this.$refs.companyField.resetValidation();
                }
                // Best effort: no ref on password, but form revalidates without value
            }
        },
    },
    methods: {
        ...mapGetters(moduleName, [
            'getSalutation',
            'getFirstName',
            'getLastName',
            'getEmail',
            'getPhone',
            'getPassword',
            'getAskPassword',
            'getCompany',
            'getUid',
        ]),
        ...mapMutations([
            'setSalutation',
            'setFirstName',
            'setLastName',
            'setEmail',
            'setPhone',
            'setPassword',
            'setAskPassword',
            'setCompany',
            'setUid',
        ]),
        ...mapMutations({
            resetErrorsMutation: 'resetErrors',
        }),
        resetErrors() {
            this.resetErrorsMutation();
        },
        validate() {
            return this.validUid && this.$refs.form.validate();
        },
        validateUid(value) {
            if (value === null || !value.length) {
                this.validUid = true;
                this.errorUidMessages = null;

                return {};
            }
            let that = this;
            let billingCountry;
            let generalCountry = this.$country.getCountry();
            if (this.$store.state['BillingAddressForm']) {
                billingCountry = this.$store.getters['BillingAddressForm/getCountry'];
            }
            let country = billingCountry ? billingCountry.toLowerCase() : generalCountry.toLowerCase();

            return Vue.$graphql
                .query({
                    query: gql`
                        query validateUid($uid: String!, $country: String!) {
                            validateUid(uid: $uid, country: $country) {
                                valid
                                error
                                uid
                            }
                        }
                    `,
                    variables: {
                        uid: value,
                        country: country,
                    },
                    fetchPolicy: 'no-cache',
                })
                .then(r => r.data)
                .then(data => {
                    if (data.validateUid.error) {
                        that.validUid = false;
                        that.errorUidMessages = [
                            this.$language.translate(
                                'Service derzeit nicht verfügbar. ' +
                                    'Bitte versuchen Sie es später oder wenden Sie sich an unseren Kundensupport.',
                            ),
                        ];
                    } else if (!data.validateUid.valid) {
                        that.errorUidMessages = [this.$language.translate('Invalid UID')];
                    } else {
                        that.errorUidMessages = null;
                    }
                    that.validUid = data.validateUid.valid;
                    return data.validateUid.valid;
                })
                .catch(error => {
                    // Handle graphql error when service is unavailable at all or destination doesn't exist anymore
                    that.validUid = false;
                    that.errorUidMessages = [
                        this.$language.translate(
                            'Service derzeit nicht verfügbar. ' +
                                'Bitte versuchen Sie es später oder wenden Sie sich an unseren Kundensupport.',
                        ),
                    ];

                    console.error(error);
                    return false;
                });
        },
        getCompanyLabel() {
            const baseLabel = this.$language.translate('Company');
            return !this.getIsPrivateCustomer ? `${baseLabel}*` : baseLabel;
        },
        getUidLabel() {
            const baseLabel = this.$language.translate('Tax number');
            return !this.getIsPrivateCustomer ? `${baseLabel}*` : baseLabel;
        },
    },
};
</script>
